/* eslint-disable camelcase */
import { graphql } from '@/generated/gql/gql'
import { CcOrderSource, Currency } from '@/generated/gql/graphql'
import { ccPlaceOrder21, clientInitiateCcOrder } from '@/legacyApi/ccOrder'
import { useLazyQuery, useMutation } from '@apollo/client'

import { backOff } from 'exponential-backoff'
import { useCallback, useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'

import { formatOrderDatePayload } from './orderDatePayload'

const usePlaceCanteenOrderInitiateOrderMutation = graphql(`
  mutation UsePlaceCanteenOrder_InitiateOrderMutation($input: InitiateCCOrderInput!) {
    initiateCCOrder(input: $input) {
      progressId
    }
  }
`)

const usePlaceCanteenOrderProgressQuery = graphql(`
  query UsePlaceCanteenOrder_ProgressQuery($progressId: String!) {
    ccOrderProgress(progressId: $progressId) {
      continuationUri
      detail
      initiationId
      status
    }
  }
`)

export type PlaceCCOrderPayloadItem = {
  name: string
  id: string
  itemId: string
  submenuId: number
  qty: number
  supplierLocationId: number
  item: {
    name: string
    type: string
    price: string
    image: string
  }
  options: Array<{
    id: string // Option item id
    name: string
    price: string
    option: {
      id: string // Option category id
    }
  }>
  reference: string
  arrivalTime: string
}

const normaliseCCProgressError = (ccProgressDetail: any): string => {
  let errorMessage =
    'An unexpected error occurred when placing your order. Please try again or get in contact if the issue persists.'

  if (!ccProgressDetail) return errorMessage

  if (typeof ccProgressDetail === 'object' && ccProgressDetail.errorMessage) {
    errorMessage = ccProgressDetail.errorMessage
  } else if (typeof ccProgressDetail === 'string') {
    try {
      const parsed = JSON.parse(ccProgressDetail)
      if (typeof parsed === 'object' && parsed.errorMessage) {
        errorMessage = parsed.errorMessage
      }
    } catch {
      // don't want to show the user the raw error string if in unexpected format
      return errorMessage
    }
  }

  return errorMessage
}

export const usePlaceCanteenOrder = ({
  useSynchronousOrderPlacement,
}: {
  useSynchronousOrderPlacement: boolean
}) => {
  const [runMutation, { data: mutationData, error: mutationError }] = useMutation(
    usePlaceCanteenOrderInitiateOrderMutation,
  )
  const [runQuery, { data: queryData }] = useLazyQuery(usePlaceCanteenOrderProgressQuery)

  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<string | undefined>(undefined)
  const inFlightRef = useRef(false)

  const polledProgressIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!mutationData?.initiateCCOrder.progressId) return
    if (polledProgressIdRef.current === mutationData.initiateCCOrder.progressId) return
    polledProgressIdRef.current = mutationData.initiateCCOrder.progressId

    const queryWithExponentialBackoff = async (progressId: string) => {
      try {
        await backOff(
          async () => {
            const { data: queryResponse } = await runQuery({
              variables: { progressId },
              fetchPolicy: 'network-only',
            })

            const orderPlacementStatus = queryResponse?.ccOrderProgress?.status

            if (orderPlacementStatus === 'error') {
              throw new Error(queryResponse?.ccOrderProgress.detail)
            }

            if (
              orderPlacementStatus === 'success' &&
              queryResponse?.ccOrderProgress?.continuationUri
            ) {
              const continuationUri = queryResponse.ccOrderProgress.continuationUri
              setData(continuationUri)
              return true // Stop further retries
            }

            // If neither error nor success, retry (let the backOff handle delays)
            throw new Error(
              "There's a delay processing your order. Refresh the page to check the status and retry if needed.",
            )
          },
          {
            startingDelay: 500,
            timeMultiple: 2,
            maxDelay: 15000,
            numOfAttempts: 10,
          },
        )
      } catch (err) {
        setError((err as Error).message)
      } finally {
        setLoading(false)
      }
    }

    setLoading(true)
    queryWithExponentialBackoff(mutationData.initiateCCOrder.progressId)
  }, [mutationData, runQuery])

  useEffect(() => {
    if (queryData?.ccOrderProgress?.status === 'error') {
      const errorMessage = normaliseCCProgressError(queryData.ccOrderProgress.detail)
      setError(errorMessage)
    }
  }, [queryData])

  const reset = useCallback(() => {
    setError('')
    setData(undefined)
    setLoading(false)
  }, [])

  const placeOrderInner = async (input: {
    bundle: number
    items: PlaceCCOrderPayloadItem[]
    cartTotal: number
    arrivalTime: string
    validDate: string
    validMeal: string
    currency: string
    balance: number
    subsidy: number
    unlimitedSubsidy: boolean
    language: string
    timezone?: string | null
  }) => {
    const {
      arrivalTime,
      items,
      cartTotal,
      bundle,
      validDate,
      validMeal,
      currency,
      subsidy,
      unlimitedSubsidy,
      balance,
      language,
      timezone,
    } = input
    if (!currency) throw new Error('no currency set')
    setError('')
    setData(undefined)
    setLoading(true)

    const totalToPay = unlimitedSubsidy ? 0 : Math.max(cartTotal - subsidy - balance, 0)
    const orderDate = formatOrderDatePayload(validDate, timezone)

    if (useSynchronousOrderPlacement) {
      await runMutation({
        variables: {
          input: {
            date: orderDate,
            meal: validMeal,
            timeslotStart: arrivalTime,
            ...(currency && { paymentCurrency: currency }),
            ...(totalToPay && { minimumPaymentValue: Math.round(totalToPay * 100) }),
            ...(bundle && { topupValue: Math.round(bundle * 100) }),
            items: items.map((item) => ({
              coreId: item.id,
              variationId: item.itemId,
              quantity: item.qty,
              submenuId: item.submenuId.toString(),
              vendorLocationId: item.supplierLocationId.toString(),
              options: item.options.map((option) => ({
                optionCategoryId: option.option.id,
                optionItemId: option.id,
                quantity: 1,
              })),
            })),
            orderSource: CcOrderSource.Web,
          },
        },
      }).catch(() => undefined)
      return
    }

    let newData = ''
    let error = ''

    try {
      if (totalToPay) {
        const { data } = await clientInitiateCcOrder(
          totalToPay.toFixed(2),
          bundle,
          orderDate,
          validMeal,
          items,
          arrivalTime,
          currency as Currency,
          [],
        )

        newData = `/payments?authToken=${data.payload.authToken}`
      } else {
        const { data } = await ccPlaceOrder21(
          orderDate,
          validMeal,
          items,
          arrivalTime,
          [],
          language,
        )
        // Updated to redirect to fusion confirmation page instead of legacy
        newData = `/order/confirmation/cc?transactionId=${data.payload.transactionId}`
      }
    } catch (err) {
      const errorCode = err && (err as any).data ? (err as any).data.errorCode : null
      const returnMessage = match(errorCode)
        .with('OUT_OF_STOCK', () => 'An item is out of stock')
        .with(
          '400.19',
          () =>
            'Your order was placed but we had trouble sending your order confirmation. Please contact our support tea',
        )
        .with(
          '400.25',
          () =>
            'The required quantity is higher than remaining capacity. Please, try again ordering less items.',
        )
        .otherwise((value) => {
          return typeof value === 'string' && value.includes('PASSED_CUT_OFF')
            ? 'Oops, the cut off time for this order has passed. View the next time/slot or meal'
            : 'We have run into a problem. Please check to see if your order was completed and if not, try again'
        })
      error = returnMessage
    }
    if (error) setError(error)
    if (newData) setData(newData)
    setLoading(false)
  }

  const placeOrder = async (input: Parameters<typeof placeOrderInner>[0]) => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    try {
      await placeOrderInner(input)
    } finally {
      inFlightRef.current = false
    }
  }

  return { placeOrder, reset, error: error ?? mutationError?.message, loading, data }
}
